import { alpha } from '@mui/material/styles';

/**
 * V2 Design System — Palette based on Figma tokens
 * 01 — Primary · Neutral-violet black
 * 02 — Lilac · Interactive accent
 * 03 — Peach · AI Identity
 * ----------------------------------------------------------------------
 */

// PRIMITIVE COLOR SCALES (from Figma "Primitive - Colors")

const DEEP_NAVY = {
  0: '#ffffff',
  50: '#f7f6fb',
  100: '#eceaf2',
  200: '#d8d5e5',
  300: '#b8b4cc',
  400: '#8e89a8',
  500: '#635e7a',
  600: '#47435c',
  700: '#373444',
  800: '#252230',
  900: '#081220',
};

const BLUE_SKY = {
  50: '#eff4ff',
  100: '#d6e4ff',
  200: '#a8c3f5',
  300: '#6e9eed',
  400: '#4573d2',
  500: '#2f5ab8',
  600: '#1e4299',
  700: '#102d78',
  800: '#081c57',
  900: '#030e36',
};

const RED = {
  50: '#fff0ef',
  100: '#fdd5d3',
  200: '#f9a8a5',
  300: '#f88080',
  400: '#e5443e',
  500: '#d93025',
  600: '#b8211a',
  700: '#941510',
  800: '#6e0c09',
  900: '#470505',
};

const GREEN = {
  50: '#edfaf4',
  100: '#c3efe1',
  200: '#7dd4ba',
  300: '#3db896',
  400: '#1ea87d',
  500: '#0e9e6e',
  600: '#0a8560',
  700: '#076b4d',
  800: '#054f39',
  900: '#033326',
};

const YELLOW = {
  50: '#fffbf0',
  100: '#fef0c7',
  200: '#fdd98a',
  300: '#f1bd6c',
  400: '#f8c24a',
  500: '#d4900a',
  600: '#b07208',
  700: '#8a5405',
  800: '#633a03',
  900: '#3d2202',
};

const ORANGE = {
  50: '#fefce8',
  100: '#fdedd3',
  200: '#fbdba7',
  300: '#f9c97c',
  400: '#f7b750',
  500: '#f5a524',
  600: '#c4841d',
  700: '#936316',
  800: '#62420e',
  900: '#312107',
};

const PEACH = {
  50: '#fff3f0',
  100: '#fecdbe',
  200: '#fea490',
  300: '#fe987a',
  400: '#fe755c',
  500: '#f96249',
  600: '#f04535',
  700: '#c22e20',
  800: '#8f1d12',
  900: '#5a0e08',
};

const LILAC = {
  50: '#f5f0ff',
  100: '#ede9fe',
  200: '#ddd6fe',
  300: '#c4b5fd',
  400: '#a78bfa',
  500: '#8b6fe8',
  600: '#7c3aed',
  700: '#6d28d9',
  800: '#5b21b6',
  900: '#4c1d95',
};

// Standard MUI grey (from Figma "Primitive - Colors" grey scale)
const GREY = {
  0: '#FFFFFF',
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
};

// SEMANTIC COLOR MAPPINGS (from Figma "semantic-colors")

const PRIMARY = {
  lighter: DEEP_NAVY[100],
  light: DEEP_NAVY[300],
  main: DEEP_NAVY[900], // fill/primary/enable
  dark: DEEP_NAVY[700], // fill/primary/hover
  darker: DEEP_NAVY[800],
  contrastText: DEEP_NAVY[50],
};

// Lilac is the interactive / AI accent — nav selection, focus rings, AI surfaces.
// Careful: a *secondary Button* is deliberately NOT lilac. Per Figma, `<Button
// color="secondary">` is peach, applied in overrides/components/button.tsx. So
// `sx={{ bgcolor: 'secondary.main' }}` and `<Button color="secondary">` are two
// different colors on purpose.
const SECONDARY = {
  lighter: LILAC[50],
  light100: LILAC[100],
  light: LILAC[200],
  main: LILAC[500], // fill/secondary/enable — interactive accent
  dark: LILAC[600], // fill/secondary/hover
  darker: LILAC[700], // fill/secondary/focus
  dark800: LILAC[800],
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: BLUE_SKY[50],
  light: BLUE_SKY[200],
  main: BLUE_SKY[500], // fill/info/enable
  dark: BLUE_SKY[600], // fill/info/hover
  darker: BLUE_SKY[700], // fill/info/focus
  contrastText: '#FFFFFF',
};

// contrastText is spelled out on these three: without it createTheme derives one from
// the luminance of `main`, which makes the on-color a calculation rather than a decision
// (and several overrides read `palette[color].contrastText` directly).
const SUCCESS = {
  lighter: GREEN[50],
  light: GREEN[200],
  main: GREEN[500], // fill/success/enable
  dark: GREEN[600], // fill/success/hover
  darker: GREEN[700],
  contrastText: '#FFFFFF',
};

const WARNING = {
  lighter: YELLOW[50],
  light: YELLOW[200],
  main: YELLOW[400], // fill/warning/enable
  dark: YELLOW[500], // fill/warning/focus
  darker: YELLOW[600],
  contrastText: DEEP_NAVY[900], // yellow 400 is too light to carry white text
};

const ERROR = {
  lighter: RED[50],
  light: RED[200],
  main: RED[400], // fill/error/enable
  dark: RED[500], // fill/error/hover
  darker: RED[600], // fill/error/focus
  contrastText: '#FFFFFF',
};

const JOOICE_AI = {
  main: LILAC[500],
  background: LILAC[50],
};

// COMMON palette object — mirrors v1 structure for JooiceTheme compatibility
const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  // Mapped for JooiceTheme TS compatibility (onyx/pearl required by theme.d.ts)
  onyx: {
    900: DEEP_NAVY[500],
    1000: DEEP_NAVY[600],
    1100: DEEP_NAVY[700],
    1200: DEEP_NAVY[800],
    1300: DEEP_NAVY[900],
    1400: DEEP_NAVY[900],
    1500: '#000000',
  },
  pearl: {
    100: DEEP_NAVY[50],
    200: DEEP_NAVY[50],
    300: DEEP_NAVY[100],
    400: DEEP_NAVY[200],
    500: DEEP_NAVY[300],
    600: DEEP_NAVY[400],
  },
  jooice_ai: JOOICE_AI,
  red: RED,
  yellow: YELLOW,
  green: GREEN,
  blue: BLUE_SKY,
  divider: alpha(DEEP_NAVY[300], 0.24),
  action: {
    hover: alpha(DEEP_NAVY[500], 0.08),
    selected: alpha(DEEP_NAVY[500], 0.16),
    disabled: alpha(DEEP_NAVY[500], 0.8),
    disabledBackground: alpha(DEEP_NAVY[500], 0.24),
    focus: alpha(DEEP_NAVY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
  background: {
    default: '#ffffff',
    neutral: DEEP_NAVY[50],
  },
};

export function paletteV2(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: DEEP_NAVY[900], // text/primary/default
      secondary: DEEP_NAVY[500], // text/secondary/default
      disabled: DEEP_NAVY[300], // text/secondary/disable
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF', // fill/background/background
      neutral: DEEP_NAVY[50], // fill/background/neutral
    },
    action: {
      ...COMMON.action,
      active: DEEP_NAVY[600],
    },
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: DEEP_NAVY[300],
      disabled: DEEP_NAVY[500],
    },
    background: {
      paper: DEEP_NAVY[800],
      default: DEEP_NAVY[900],
      neutral: alpha(DEEP_NAVY[500], 0.12),
    },
    action: {
      ...COMMON.action,
      active: DEEP_NAVY[400],
    },
  };

  return mode === 'light' ? light : dark;
}

// V2 gradients — deepNavy-based (replaces orange/blue gradients from v1)
export const gradientsV2 = {
  primary: {
    ltb: `linear-gradient(360deg, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
    lbt: `linear-gradient(180deg, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
    llr: `linear-gradient(270deg, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
    lrl: `linear-gradient(90deg, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
    rbl: `radial-gradient(100% 100% at 100% 0%, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
    rbr: `radial-gradient(100% 100% at 0% 0%, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
    rtl: `radial-gradient(100% 100% at 100% 100%, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
    rtr: `radial-gradient(100% 100% at 0% 100%, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
  },
  secondary: {
    ltb: `linear-gradient(360deg, ${SECONDARY.dark} 0%, ${SECONDARY.main} 100%)`,
    lbt: `linear-gradient(180deg, ${SECONDARY.dark} 0%, ${SECONDARY.main} 100%)`,
    llr: `linear-gradient(270deg, ${SECONDARY.dark} 0%, ${SECONDARY.main} 100%)`,
    lrl: `linear-gradient(90deg, ${SECONDARY.dark} 0%, ${SECONDARY.main} 100%)`,
    rbl: `radial-gradient(100% 100% at 100% 0%, ${SECONDARY.dark} 0%, ${SECONDARY.main} 100%)`,
    rbr: `radial-gradient(100% 100% at 0% 0%, ${SECONDARY.dark} 0%, ${SECONDARY.main} 100%)`,
    rtl: `radial-gradient(100% 100% at 100% 100%, ${SECONDARY.dark} 0%, ${SECONDARY.main} 100%)`,
    rtr: `radial-gradient(100% 100% at 0% 100%, ${SECONDARY.dark} 0%, ${SECONDARY.main} 100%)`,
  },
};

// Export primitives for use in overrides and component themes
export const deepNavy = DEEP_NAVY;
export const blueSky = BLUE_SKY;
export const redScale = RED;
export const greenScale = GREEN;
export const yellowScale = YELLOW;
export const orangeScale = ORANGE;
export const peachScale = PEACH;
export const lilacScale = LILAC;
